import { useEffect, useRef, useState } from 'react';

interface CountdownTimerProps {
  /** Data/hora final do prazo (ISO 8601). */
  prazoEm: string;
}

type Estado = 'normal' | 'urgente' | 'atrasado';

const UM_DIA_MS = 24 * 60 * 60 * 1000;

function partes(ms: number) {
  const total = Math.floor(Math.abs(ms) / 1000);
  return {
    dias: Math.floor(total / 86400),
    horas: Math.floor((total % 86400) / 3600),
    min: Math.floor((total % 3600) / 60),
    seg: total % 60,
  };
}

const pad = (n: number) => String(n).padStart(2, '0');

const fmtData = new Intl.DateTimeFormat('pt-BR', {
  weekday: 'short',
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
});

/**
 * Contador regressivo do prazo de entrega, para ficar flutuando na tela 3D.
 * Quando o prazo encerra, exibe um aviso "ATRASADO" do tamanho do contador
 * inteiro por ~5 segundos, e depois passa a contar o tempo de atraso.
 */
export function CountdownTimer({ prazoEm }: CountdownTimerProps) {
  const alvo = new Date(prazoEm).getTime();
  const valido = Number.isFinite(alvo);

  const [agora, setAgora] = useState(() => Date.now());
  const [flash, setFlash] = useState(false);
  const eraAtrasado = useRef<boolean | null>(null);
  const flashTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Tique de 1 segundo.
  useEffect(() => {
    const id = setInterval(() => setAgora(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  const restante = valido ? alvo - agora : 0;
  const atrasado = restante <= 0;

  // Dispara o flash quando passa a estar atrasado (ou ja abre atrasado).
  useEffect(() => {
    if (!valido) return;
    const antes = eraAtrasado.current;
    if (atrasado && antes !== true) {
      setFlash(true);
      if (flashTimer.current) clearTimeout(flashTimer.current);
      flashTimer.current = setTimeout(() => setFlash(false), 5000);
    }
    eraAtrasado.current = atrasado;
  }, [atrasado, valido]);

  useEffect(() => () => { if (flashTimer.current) clearTimeout(flashTimer.current); }, []);

  if (!valido) return null;

  const estado: Estado = atrasado ? 'atrasado' : restante <= UM_DIA_MS ? 'urgente' : 'normal';
  const p = partes(restante);

  return (
    <div className={`countdown ${estado}`} role="timer" aria-live="polite">
      <div className="cd-titulo">
        {atrasado ? 'Prazo esgotado' : 'Prazo de entrega'}
      </div>

      <div className="cd-relogio">
        <Unidade valor={p.dias} rotulo="dias" />
        <span className="cd-sep">:</span>
        <Unidade valor={p.horas} rotulo="horas" pad />
        <span className="cd-sep">:</span>
        <Unidade valor={p.min} rotulo="min" pad />
        <span className="cd-sep">:</span>
        <Unidade valor={p.seg} rotulo="seg" pad />
      </div>

      <div className="cd-data">
        {atrasado ? 'Venceu em ' : 'Entrega: '}
        <b>{fmtData.format(alvo)}</b>
      </div>

      {atrasado && !flash && (
        <div className="cd-atraso-msg">⚠ Atrasado há {p.dias}d {pad(p.horas)}h {pad(p.min)}m</div>
      )}

      {flash && (
        <div className="cd-flash">
          <div className="cd-flash-txt">ATRASADO!</div>
        </div>
      )}
    </div>
  );
}

function Unidade({ valor, rotulo, pad: usePad }: { valor: number; rotulo: string; pad?: boolean }) {
  return (
    <div className="cd-unidade">
      <span className="cd-num">{usePad ? pad(valor) : valor}</span>
      <span className="cd-rot">{rotulo}</span>
    </div>
  );
}
